import { useMemo } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useIxcCorpactionsCaadminV1PaymentQueryServicePaymentGet } from '@interxfi/api-client/generated/hooks';
import { EntityActionMenu, type EntityActionMenuConfig } from '@interxfi/app-components/components/entity-action-menu/entity-action-menu';
import { PageHeader } from '@interxfi/app-components/components/layout/page-header/page-header';
import {
  DEFAULT_PAGE_LAYOUT_GAP,
  DEFAULT_PAGE_LAYOUT_MAX_WIDTH_CONTAINER_SIZE,
  PageLayout,
} from '@interxfi/app-components/components/layout/page-layout/page-layout';
import { AppReadonlyList } from '@interxfi/app-components/components/readonly-list/app-readonly-list';
import { AppReadonlyListStack } from '@interxfi/app-components/components/readonly-list/app-readonly-list-stack';
import { BoxedAppReadonlyList } from '@interxfi/app-components/components/readonly-list/boxed-app-readonly-list';
import { type AggregateBreadcrumb, buildBreadcrumb } from '@interxfi/app-components/components/router-breadcrumbs/router-breadcrumbs';
import { HorizontalStack } from '@interxfi/design-system/components/alignment/horizontal-stack';
import { VerticalStack } from '@interxfi/design-system/components/alignment/vertical-stack';
import { CopyButton } from '@interxfi/design-system/components/copy-button/copy-button';
import { DEFAULT_WRAPPING_LAYOUT_COLUMN, LAYOUT_ZERO_CONTAINER_PADDING } from '@interxfi/design-system/components/layout-container';
import { WrappingLayoutContainer } from '@interxfi/design-system/components/layout-container/wrapping/wrapping-layout-container';
import type { ReadonlyListGroup } from '@interxfi/design-system/components/list/readonly/stacked/readonly-list-stack';
import { Typography } from '@interxfi/design-system/components/typography';
import type { IxcCorpactionsCaadminV1PaymentState } from '@interxfi/types';
import {
  CorpactCaAdminPaymentEventDrawer,
  CorpactCaAdminPaymentEventSummary,
} from '../../../../../../../../../../features/corporate-action/ca-admin/payment/ca-admin-payment-event';
import { getCorpactCaAdminPaymentStateListItems } from '../../../../../../../../../../features/corporate-action/ca-admin/payment/ca-admin-payment-list-items';
import { getCorpactCaAdminPaymentDetailPath } from '../../../../../../../../../../features/corporate-action/ca-admin/payment/ca-admin-payment-routing';
import { CorpactCaAdminPaymentStatusPill } from '../../../../../../../../../../features/corporate-action/ca-admin/payment/ca-admin-payment-status-pill';
import { CorpactCaAdminPaymentSummaryBar } from '../../../../../../../../../../features/corporate-action/ca-admin/payment/ca-admin-payment-summary-bar';
import { buildDefaultErrorMessageGetter, useErrorHandler } from '../../../../../../../../../../util/error-handler';
import { getPageHead } from '../../../../../../../../../../util/routing/meta';

export const Route = createFileRoute('/_app/_clearing-core/_clearing-required/clearing/operations/_operations/ca/payment/_payment/$paymentId')({
  loader: ({ params }) =>
    ({
      breadcrumb: buildBreadcrumb({
        id: `corporate-action-payment-${params.paymentId}`,
        label: 'Payment',
        to: getCorpactCaAdminPaymentDetailPath(params.paymentId),
        suffix: <CopyButton content={params.paymentId} label="Copy Payment ID" tooltip={{ copy: params.paymentId, copied: 'Copied Payment ID' }} />,
      }),
    }) satisfies AggregateBreadcrumb,
  head: () => getPageHead('Corporate Action Payment'),
  component: CorporateActionPaymentDetail,
});

function getListItems(data: IxcCorpactionsCaadminV1PaymentState | undefined) {
  const items = getCorpactCaAdminPaymentStateListItems(data);

  return {
    general: {
      left: [items.data.expectedPaymentDate, items.data.amount.formatted, items.data.amount.currency],
      right: [items.paymentId, items.actionId, items.assetId],
    },
    dtc: data?.data?.dtc
      ? ([
          {
            id: 'dtcHeader',
            items: [items.data.dtc.dtcHeader.eventId, items.data.dtc.dtcHeader.paymentId, items.data.dtc.dtcHeader.underlyingCusip],
          },
          {
            id: 'dtcMain',
            items: [
              items.data.dtc.corporateActionEventIdentification,
              items.data.dtc.officialCorporateActionEventIdentification,
              items.data.dtc.movementConfirmationIdentification,
              items.data.dtc.previousMovementPreliminaryAdviceIdentification,
              items.data.dtc.previousMovementConfirmationIdentification,
              items.data.dtc.lastProcessingStatusAt,
            ],
          },
        ] as const satisfies ReadonlyListGroup[])
      : undefined,
  };
}

function getMenuItems(paymentId: string) {
  return {
    eventDrawer: {
      type: 'drawer',
      drawer: {
        Component: CorpactCaAdminPaymentEventDrawer,
        props: { request: { paymentId } },
      },
    },
    actions: undefined,
  } as const satisfies EntityActionMenuConfig;
}

const errorMessageGetter = buildDefaultErrorMessageGetter('Failed to load corporate action payment', 'view this corporate action payment');

function CorporateActionPaymentDetail() {
  const { paymentId } = Route.useParams();
  const request = { paymentId } as const;
  const { data, error, isPending } = useIxcCorpactionsCaadminV1PaymentQueryServicePaymentGet(request);
  useErrorHandler(error, errorMessageGetter);
  const showPending = Boolean(isPending || error);
  const listItems = useMemo(() => getListItems(data?.payment), [data?.payment]);
  const menuItems = useMemo(() => getMenuItems(paymentId), [paymentId]);

  return (
    <PageLayout gap={DEFAULT_PAGE_LAYOUT_GAP} maxWidthContainer={DEFAULT_PAGE_LAYOUT_MAX_WIDTH_CONTAINER_SIZE}>
      <PageLayout.Section>
        <VerticalStack gap={7}>
          <PageHeader>
            <HorizontalStack gap={2} wrap="nowrap">
              <CorpactCaAdminPaymentStatusPill isPending={showPending} size="large" status={data?.payment?.status} />
            </HorizontalStack>

            <EntityActionMenu {...menuItems} />
          </PageHeader>

          <CorpactCaAdminPaymentSummaryBar {...request} />

          <VerticalStack gap={4}>
            <Typography.Level3>Events</Typography.Level3>

            <CorpactCaAdminPaymentEventSummary gap={DEFAULT_PAGE_LAYOUT_GAP} padding={LAYOUT_ZERO_CONTAINER_PADDING} request={request} />
          </VerticalStack>
        </VerticalStack>
      </PageLayout.Section>

      <PageLayout.Section>
        <VerticalStack gap={4}>
          <Typography.Level3>Data</Typography.Level3>

          <WrappingLayoutContainer
            gap={DEFAULT_PAGE_LAYOUT_GAP}
            left={{
              ...DEFAULT_WRAPPING_LAYOUT_COLUMN,
              render: <AppReadonlyList isPending={showPending} items={listItems.general.left} />,
            }}
            padding={LAYOUT_ZERO_CONTAINER_PADDING}
            right={{
              ...DEFAULT_WRAPPING_LAYOUT_COLUMN,
              render: <BoxedAppReadonlyList isPending={showPending} items={listItems.general.right} />,
            }}
            wrap="forward"
            wrapGap={7}
          />
        </VerticalStack>
      </PageLayout.Section>

      {listItems.dtc ? (
        <PageLayout.Section>
          <VerticalStack gap={4}>
            <Typography.Level3>DTC Data</Typography.Level3>
          </VerticalStack>

          <AppReadonlyListStack groups={listItems.dtc} isPending={showPending} />
        </PageLayout.Section>
      ) : null}
    </PageLayout>
  );
}
